import { useCallback, useEffect, useRef, useState } from "react";
import { Disclosure, DisclosurePanel } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Typography } from "@/components/ui/atoms";

interface ArticleFaqProps {
  faqs: Array<{ question: string; answer: string }>;
}

const VIEWPORT_MARGIN_PX = 16;

const ANSWER_PROSE_CLASS =
  "pb-4 text-base leading-7 text-dark-invirtus/90 [&_a]:text-dark-invirtus [&_a]:underline [&_a]:underline-offset-4 [&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-3 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6";

function isFullyVisibleInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= VIEWPORT_MARGIN_PX &&
    rect.bottom <= window.innerHeight - VIEWPORT_MARGIN_PX
  );
}

function getFaqPanelId(index: number): string {
  return `article-faq-panel-${index}`;
}

export function ArticleFaq({ faqs }: ArticleFaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const panelRefs = useRef<(HTMLElement | null)[]>([]);

  const handleToggle = useCallback((index: number) => {
    setOpenIndex((current) => (current === index ? null : index));
  }, []);

  useEffect(() => {
    if (openIndex === null) return;

    const frameId = requestAnimationFrame(() => {
      const panel = panelRefs.current[openIndex];
      if (!panel || isFullyVisibleInViewport(panel)) return;

      panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });

    return () => cancelAnimationFrame(frameId);
  }, [openIndex]);

  if (!faqs?.length) return null;

  return (
    <section
      aria-labelledby="article-faq-heading"
      className="border-t border-dark-invirtus-soft px-5 py-8 md:px-8"
    >
      <div className="mx-auto max-w-[70ch]">
        <Typography
          id="article-faq-heading"
          as="h2"
          variant="h2"
          color="primary"
          weight="bold"
          className="mb-6 text-[1.55rem] leading-tight"
        >
          Frequently Asked Questions
        </Typography>

        <div className="divide-y divide-dark-invirtus-soft border-y border-dark-invirtus-soft">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const panelId = getFaqPanelId(index);

            return (
              <Disclosure key={`${faq.question}-${index}`} as="div">
                <h3 className="m-0">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => handleToggle(index)}
                    className="group flex w-full items-center justify-between gap-4 py-4 text-left transition-colors hover:text-dark-invirtus/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/40"
                  >
                    <Typography
                      as="span"
                      variant="body-md"
                      color="primary"
                      weight="semibold"
                      className="flex-1 text-base leading-6"
                    >
                      {faq.question}
                    </Typography>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-dark-invirtus/60 transition-transform duration-200 motion-reduce:transition-none ${
                        isOpen ? "rotate-180" : ""
                      }`}
                      aria-hidden
                    />
                  </button>
                </h3>

                {isOpen ? (
                  <div className="overflow-hidden">
                    <DisclosurePanel
                      static
                      id={panelId}
                      ref={(element) => {
                        panelRefs.current[index] = element;
                      }}
                      className={ANSWER_PROSE_CLASS}
                    >
                      <ReactMarkdown>{faq.answer || ""}</ReactMarkdown>
                    </DisclosurePanel>
                  </div>
                ) : null}
              </Disclosure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
